/**
 * Real-time Collaboration Hub Page
 * For team collaboration on safety items with comments, pins, and presence
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  MessageSquare, Pin, Users, Activity, Send, Clock, Eye, Edit2,
  Trash2, ThumbsUp, CheckCircle, AlertTriangle, RefreshCw, Bell,
  FileText, Link, Plus, Search, MoreVertical, Paperclip, AtSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Entity types for collaboration
const ENTITY_TYPES = [
  { value: 'incident', label: 'Incidents', color: 'bg-red-500' },
  { value: 'risk', label: 'Risks', color: 'bg-orange-500' },
  { value: 'audit', label: 'Audits', color: 'bg-blue-500' },
  { value: 'capa', label: 'CAPAs', color: 'bg-purple-500' },
  { value: 'change', label: 'Changes', color: 'bg-cyan-500' },
  { value: 'meeting', label: 'Meetings', color: 'bg-green-500' }
];

// Priority colors for pins
const PIN_PRIORITY_COLORS = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-blue-500'
};

export default function CollaborationHubPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('activity');
  const [activities, setActivities] = useState([]);
  const [myActivities, setMyActivities] = useState([]);
  const [comments, setComments] = useState([]);
  const [pins, setPins] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Selected entity for viewing
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityType, setEntityType] = useState('incident');
  const [entityId, setEntityId] = useState('');
  
  // Comment form
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  
  // Pin form
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinForm, setPinForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    evidence_url: '',
    evidence_type: 'document'
  });

  const token = localStorage.getItem('token');
  const wsRef = useRef(null);

  useEffect(() => {
    fetchRecentActivities();
    fetchMyActivities();
  }, []);

  useEffect(() => {
    if (selectedEntity) {
      fetchEntityCollaboration();
      connectWebSocket();
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [selectedEntity]);

  const fetchRecentActivities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/collaboration/activities/recent?hours=48&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActivities(data.activities || []);
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyActivities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/collaboration/activities/user/me?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyActivities(data.activities || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch my activities:', error);
    }
  };

  const fetchEntityCollaboration = async () => {
    if (!selectedEntity) return;
    
    try {
      // Fetch comments
      const commentsRes = await fetch(
        `${API_URL}/api/collaboration/comments/${selectedEntity.type}/${selectedEntity.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (commentsRes.ok) {
        const data = await commentsRes.json();
        setComments(data.comments || []);
      }

      // Fetch pins
      const pinsRes = await fetch(
        `${API_URL}/api/collaboration/pins/${selectedEntity.type}/${selectedEntity.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (pinsRes.ok) {
        const data = await pinsRes.json();
        setPins(data.pins || []);
      }

      // Fetch active users
      const usersRes = await fetch(
        `${API_URL}/api/collaboration/presence/${selectedEntity.type}/${selectedEntity.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (usersRes.ok) {
        const data = await usersRes.json();
        setActiveUsers(data.active_users || []);
      }

      // Join presence
      await fetch(`${API_URL}/api/collaboration/presence/join?entity_type=${selectedEntity.type}&entity_id=${selectedEntity.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to fetch entity collaboration:', error);
    }
  };

  const connectWebSocket = () => {
    if (!selectedEntity) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Get WebSocket URL from API URL
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/collab/${selectedEntity.type}/${selectedEntity.id}?token=${token}`);

    ws.onopen = () => {
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      handleWebSocketMessage(message);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    wsRef.current = ws;
  };

  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'comment_new':
        setComments(prev => [...prev, message.comment]);
        toast.info(`New comment from ${message.comment.author_name}`);
        break;
      case 'comment_update':
        setComments(prev => prev.map(c => c.id === message.comment.id ? message.comment : c));
        break;
      case 'comment_delete':
        setComments(prev => prev.filter(c => c.id !== message.comment_id));
        break;
      case 'pin_new':
        setPins(prev => [...prev, message.pin]);
        toast.info(`New pin: ${message.pin.title}`);
        break;
      case 'presence_join':
        setActiveUsers(prev => {
          if (!prev.find(u => u.user_id === message.user_id)) {
            return [...prev, { user_id: message.user_id, user_name: message.user_name, user_role: message.user_role }];
          }
          return prev;
        });
        break;
      case 'presence_leave':
        setActiveUsers(prev => prev.filter(u => u.user_id !== message.user_id));
        break;
      default:
        console.log('Unknown message type:', message.type);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !selectedEntity) return;

    try {
      const payload = {
        entity_type: selectedEntity.type,
        entity_id: selectedEntity.id,
        content: newComment,
        parent_comment_id: replyTo?.id || null,
        mentions: []
      };

      const res = await fetch(`${API_URL}/api/collaboration/comments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const comment = await res.json();
        setComments(prev => [...prev, comment]);
        setNewComment('');
        setReplyTo(null);
        toast.success('Comment added');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleCreatePin = async () => {
    if (!pinForm.title.trim() || !selectedEntity) return;

    try {
      const payload = {
        entity_type: selectedEntity.type,
        entity_id: selectedEntity.id,
        ...pinForm
      };

      const res = await fetch(`${API_URL}/api/collaboration/pins`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const pin = await res.json();
        setPins(prev => [...prev, pin]);
        setShowPinModal(false);
        setPinForm({ title: '', description: '', priority: 'medium', evidence_url: '', evidence_type: 'document' });
        toast.success('Evidence pinned');
      }
    } catch (error) {
      toast.error('Failed to pin evidence');
    }
  };

  const handleAddReaction = async (commentId, emoji) => {
    try {
      const res = await fetch(`${API_URL}/api/collaboration/comments/${commentId}/reactions/${emoji}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const updated = await res.json();
        setComments(prev => prev.map(c => c.id === commentId ? updated : c));
      }
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  };

  const handleSelectEntity = () => {
    if (entityType && entityId) {
      setSelectedEntity({ type: entityType, id: entityId });
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-sky-500 mx-auto mb-2" />
          <p className="text-gray-400">Loading collaboration data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="collaboration-hub-page">
      <h1 className="text-3xl font-bold text-white mb-4">{t('collaboration.title')}</h1>
      <p className="text-gray-300 mb-6">{t('collaboration.subtitle')}</p>
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-sky-500" />
            <span className="text-xl font-semibold text-white">{t('collaboration.title')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="flex items-center gap-1 text-white border-gray-600">
              <Bell className="h-3 w-3" />
              {unreadCount} unread
            </Badge>
            <Button variant="outline" onClick={fetchRecentActivities} className="text-white border-gray-600">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Entity Selection & Collaboration Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Entity Selector */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Select Item to Collaborate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Select value={entityType} onValueChange={setEntityType}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map(et => (
                      <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Enter item ID or number"
                  value={entityId}
                  onChange={(e) => setEntityId(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handleSelectEntity} disabled={!entityId}>
                  <Search className="h-4 w-4 mr-2" />
                  Load
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Collaboration Area */}
          {selectedEntity ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Badge className={ENTITY_TYPES.find(e => e.value === selectedEntity.type)?.color}>
                      {selectedEntity.type.toUpperCase()}
                    </Badge>
                    {selectedEntity.id}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {activeUsers.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Eye className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-gray-500">{activeUsers.length} viewing</span>
                        <div className="flex -space-x-2 ml-2">
                          {activeUsers.slice(0, 3).map((user, i) => (
                            <Avatar key={i} className="h-6 w-6 border-2 border-white">
                              <AvatarFallback className="text-[10px] bg-sky-500 text-white">
                                {getInitials(user.user_name)}
                              </AvatarFallback>
                            </Avatar>
                          ))}
                          {activeUsers.length > 3 && (
                            <span className="text-xs text-gray-500 ml-1">+{activeUsers.length - 3}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="comments">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="comments">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Comments ({comments.length})
                    </TabsTrigger>
                    <TabsTrigger value="pins">
                      <Pin className="h-4 w-4 mr-2" />
                      Evidence ({pins.length})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="comments" className="space-y-4">
                    <ScrollArea className="h-[400px] pr-4">
                      {comments.length > 0 ? (
                        <div className="space-y-4">
                          {comments.filter(c => !c.parent_comment_id).map((comment) => (
                            <div key={comment.id} className="border rounded-lg p-3">
                              <div className="flex items-start gap-3">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="bg-sky-500 text-white text-xs">
                                    {getInitials(comment.author_name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-sm">{comment.author_name}</span>
                                    {comment.author_role && (
                                      <Badge variant="outline" className="text-[10px]">{comment.author_role}</Badge>
                                    )}
                                    <span className="text-xs text-gray-500">{formatTimeAgo(comment.created_at)}</span>
                                    {comment.is_edited && (
                                      <span className="text-xs text-gray-400">(edited)</span>
                                    )}
                                  </div>
                                  <p className="text-sm mt-1">{comment.content}</p>
                                  
                                  {/* Reactions */}
                                  <div className="flex items-center gap-2 mt-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2"
                                      onClick={() => handleAddReaction(comment.id, '👍')}
                                    >
                                      <ThumbsUp className="h-3 w-3 mr-1" />
                                      {comment.reactions?.['👍']?.length || 0}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2"
                                      onClick={() => setReplyTo(comment)}
                                    >
                                      Reply
                                    </Button>
                                    {comment.is_resolved && (
                                      <Badge className="bg-green-500 text-xs">
                                        <CheckCircle className="h-3 w-3 mr-1" /> Resolved
                                      </Badge>
                                    )}
                                  </div>

                                  {/* Replies */}
                                  {comment.reply_count > 0 && (
                                    <div className="ml-4 mt-3 border-l-2 pl-3 space-y-2">
                                      {comments
                                        .filter(c => c.parent_comment_id === comment.id)
                                        .map(reply => (
                                          <div key={reply.id} className="text-sm">
                                            <div className="flex items-center gap-2">
                                              <span className="font-medium">{reply.author_name}</span>
                                              <span className="text-xs text-gray-500">{formatTimeAgo(reply.created_at)}</span>
                                            </div>
                                            <p className="text-gray-600 dark:text-gray-300">{reply.content}</p>
                                          </div>
                                        ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>No comments yet. Start the discussion!</p>
                        </div>
                      )}
                    </ScrollArea>

                    {/* Comment Input */}
                    <div className="pt-4 border-t">
                      {replyTo && (
                        <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 p-2 rounded mb-2">
                          <span className="text-sm text-gray-500">
                            Replying to {replyTo.author_name}
                          </span>
                          <Button variant="ghost" size="sm" onClick={() => setReplyTo(null)}>×</Button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Textarea
                          placeholder="Add a comment... Use @mention to notify team members"
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          className="min-h-[60px]"
                        />
                        <Button onClick={handleSubmitComment} disabled={!newComment.trim()} data-testid="send-comment-btn">
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="pins" className="space-y-4">
                    <div className="flex justify-end">
                      <Button onClick={() => setShowPinModal(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Pin Evidence
                      </Button>
                    </div>

                    <ScrollArea className="h-[400px]">
                      {pins.length > 0 ? (
                        <div className="space-y-3">
                          {pins.map((pin) => (
                            <div key={pin.id} className="border rounded-lg p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className={`p-2 rounded ${PIN_PRIORITY_COLORS[pin.priority]} text-white`}>
                                    <Paperclip className="h-4 w-4" />
                                  </div>
                                  <div>
                                    <h4 className="font-semibold">{pin.title}</h4>
                                    <p className="text-sm text-gray-500">{pin.description}</p>
                                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                                      <span>Pinned by {pin.pinned_by_name}</span>
                                      <span>•</span>
                                      <span>{formatTimeAgo(pin.created_at)}</span>
                                      {pin.view_count > 0 && (
                                        <>
                                          <span>•</span>
                                          <span><Eye className="h-3 w-3 inline" /> {pin.view_count}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <Badge className={PIN_PRIORITY_COLORS[pin.priority]}>
                                  {pin.priority}
                                </Badge>
                              </div>
                              {pin.evidence_url && (
                                <a
                                  href={pin.evidence_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-sm text-sky-500 hover:underline mt-2"
                                >
                                  <Link className="h-3 w-3" />
                                  View Evidence
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          <Pin className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>No evidence pinned yet</p>
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card className="p-8 text-center text-gray-500">
              <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <h3 className="font-semibold text-lg mb-2">Select an Item to Collaborate</h3>
              <p>Choose an entity type and enter its ID to view and add comments, pins, and see who's working on it.</p>
            </Card>
          )}
        </div>

        {/* Activity Feed */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-sky-500" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                {activities.length > 0 ? (
                  <div className="space-y-3">
                    {activities.map((activity, i) => (
                      <div
                        key={activity.id || i}
                        className="flex items-start gap-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                        onClick={() => setSelectedEntity({ type: activity.entity_type, id: activity.entity_id })}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-gray-200 text-gray-600 text-xs">
                            {getInitials(activity.actor_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">
                            <span className="font-medium">{activity.actor_name}</span>
                            {' '}
                            {activity.activity_type === 'comment_added' && 'commented on'}
                            {activity.activity_type === 'mention' && 'mentioned someone in'}
                            {activity.activity_type === 'evidence_pinned' && 'pinned evidence to'}
                            {activity.activity_type === 'comment_reply' && 'replied to a comment on'}
                            {' '}
                            <Badge variant="outline" className="text-[10px]">
                              {activity.entity_type}
                            </Badge>
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {activity.details?.comment_preview || activity.details?.pin_title || activity.entity_id}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimeAgo(activity.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No recent activity</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Pin Evidence Modal */}
      <Dialog open={showPinModal} onOpenChange={setShowPinModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pin Evidence</DialogTitle>
            <DialogDescription>
              Attach important evidence or documentation to this item
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input
                value={pinForm.title}
                onChange={(e) => setPinForm({ ...pinForm, title: e.target.value })}
                placeholder="e.g., Maintenance Log March 2024"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={pinForm.description}
                onChange={(e) => setPinForm({ ...pinForm, description: e.target.value })}
                placeholder="Brief description of this evidence..."
              />
            </div>
            <div>
              <label className="text-sm font-medium">Priority</label>
              <Select value={pinForm.priority} onValueChange={(v) => setPinForm({ ...pinForm, priority: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Evidence URL (optional)</label>
              <Input
                value={pinForm.evidence_url}
                onChange={(e) => setPinForm({ ...pinForm, evidence_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPinModal(false)}>Cancel</Button>
            <Button onClick={handleCreatePin} disabled={!pinForm.title.trim()}>
              <Pin className="h-4 w-4 mr-2" />
              Pin Evidence
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
